import React, { useState, useEffect, useRef } from 'react';
import { withRouter, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import bgLogin from '../../assets/images/bg_login.jpg';
import logoNexus from '../../assets/images/logo_nexus_small.png';
import { useAuth, APP_VERSION_LABEL } from '../../utils';
import * as s from './styled-login';

// Chave do localStorage usada pra persistir o ultimo username submetido
// (so quando o operador marca 'Manter conectado'). Senha NUNCA persiste.
const REMEMBER_USERNAME_KEY = 'cco:last-username';

// Regex simples pra email — RFC 5322 e complexo demais; este pega 99%
// dos casos reais e e o que a maioria dos servidores usa.
// Aceita: a@b.c, user.name+tag@sub.domain.com.br
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const looksLikeEmail = (v) => v.includes('@');
const isValidEmail = (v) => EMAIL_RE.test(v);

// Icones inline Lucide-style — match exato com Login.html (stroke
// currentColor, sem dependencia de icon library).
const IUser = (
  <svg
    className="lead"
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="8" r="3.5" />
    <path d="M5 20c1.5-3.5 4-5 7-5s5.5 1.5 7 5" />
  </svg>
);
const ILock = (
  <svg
    className="lead"
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="5" y="11" width="14" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);
const IEye = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const IEyeOff = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.7"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m3 3 18 18" />
    <path d="M10.6 6.2A10 10 0 0 1 12 6c6 0 10 6 10 6a14.5 14.5 0 0 1-3.3 3.6M6.6 6.6A14 14 0 0 0 2 12s4 7 10 7c1.5 0 2.8-.3 4-.7" />
    <path d="M14 14a3 3 0 1 1-4-4" />
  </svg>
);
const IArrow = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h14M13 5l7 7-7 7" />
  </svg>
);
const ICheckMark = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m4 12 5 5 11-12" />
  </svg>
);
const IBolt = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M13 3 4 14h7l-1 7 9-11h-7z" />
  </svg>
);
const IAlert = (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 8v4M12 16v.5" />
  </svg>
);

const Login = () => {
  const history = useHistory();
  // dispatch usado SOMENTE pra limpar reduxError em handleUsernameChange/
  // handlePasswordChange (compat com codigo legado que pode dispatchar
  // USUARIO/SET_ERROR fora do useAuth). signIn em si dispatcha sozinho.
  const dispatch = useDispatch();
  // useAuth centraliza: Auth.signIn, dispatch Redux, loading, rate
  // limit (3 falhas -> cooldown 30s), e mensagem PT-BR de erro.
  const {
    signIn,
    loading,
    error: authError,
    cooldownLeftSec,
    inCooldown,
  } = useAuth();
  // Restaura ultimo username do localStorage (se existir). Senha NUNCA
  // e persistida — operador sempre digita. Restore acontece UMA vez no
  // mount inicial via lazy initializer.
  const [username, setUsername] = useState(() => {
    try {
      return localStorage.getItem(REMEMBER_USERNAME_KEY) || '';
    } catch {
      return '';
    }
  });
  const [password, setPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [remember, setRemember] = useState(true);
  // Caps Lock detection — bug operacional comum (operador digita senha
  // toda em maiuscula sem perceber). Detectamos via getModifierState
  // no keydown/keyup do password input. Mostra chip de aviso inline
  // enquanto a flag estiver ativa.
  const [capsLockOn, setCapsLockOn] = useState(false);

  // Refs pros inputs pra fazer auto-focus no mount no primeiro campo
  // vazio. Quando o operador volta a tela e ja tem username lembrado,
  // o foco vai direto pra senha — fluxo 1 keystroke a menos.
  const usernameInputRef = useRef(null);
  const passwordInputRef = useRef(null);
  useEffect(() => {
    if (!username) {
      usernameInputRef.current?.focus();
    } else {
      passwordInputRef.current?.focus();
    }
    // Roda so no mount — refs sao estaveis, username inicial pode ser
    // do localStorage mas nao queremos refocar a cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Preload do bg image — sem isso, o usuario via flash preto antes
  // da imagem aparecer (download serial atras de CSS). <link rel=preload>
  // injetado no head dispara request com prioridade alta paralelo ao
  // CSS. Remove no unmount pra nao acumular tags se o user voltar a tela.
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = bgLogin;
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);
  const handlePassKeyEvent = (e) => {
    if (typeof e.getModifierState === 'function') {
      setCapsLockOn(e.getModifierState('CapsLock'));
    }
  };
  // error: validacao local OU mensagem do useAuth (que ja dispatchou
  // pro Redux). reduxError mantido pra compat com legado que possa
  // dispatchar USUARIO/SET_ERROR direto fora do hook.
  const reduxError = useSelector((state) => state.usuario.error || '');
  const [localError, setLocalError] = useState('');
  const error = localError || authError || reduxError;

  const handleLogin = () => {
    signIn(username, password)
      .then((user) => {
        // Persistencia do username (so se 'Manter conectado' ligado).
        // Senha jamais persiste.
        try {
          if (remember) {
            localStorage.setItem(REMEMBER_USERNAME_KEY, username);
          } else {
            localStorage.removeItem(REMEMBER_USERNAME_KEY);
          }
        } catch {
          // localStorage indisponivel (privacy mode) — ignora.
        }
        // Sanity: zera senha apos sucesso.
        setPassword('');
        if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
          // Passa email/senha via state da rota (nao URL) — credenciais
          // nao ficam em historico do navegador nem em logs de servidor.
          history.push('/signup', {
            email: username,
            password,
          });
        } else {
          // SPA navigation: muito mais rapido que reload e evita "tela
          // branca" enquanto o /me do user (que pode demorar 30s+) carrega.
          history.push('/dashboard');
        }
      })
      .catch(() => {
        // Erro ja foi processado pelo useAuth (dispatch + setError +
        // failCount). .catch presente so pra evitar 'unhandled rejection'.
      });
  };

  // Reset de erro ao editar campos — comportamento do legado preservado
  // (operador nao fica com mensagem antiga "Senha incorreta" enquanto ja
  // ta digitando senha nova).
  const handleUsernameChange = (value) => {
    setUsername(value);
    if (localError) setLocalError('');
    if (value.length === 0 && reduxError) {
      dispatch({ type: 'USUARIO/SET_ERROR', erro: '' });
    }
  };
  const handlePasswordChange = (value) => {
    setPassword(value);
    if (localError) setLocalError('');
    if (value.length === 0 && reduxError) {
      dispatch({ type: 'USUARIO/SET_ERROR', erro: '' });
    }
  };

  // Honeypot field — input escondido fora da viewport. Bots de
  // form-fill auto preenchem todos os campos visiveis e invisiveis;
  // usuario real nunca interage com esse. Se vier preenchido, bot.
  // Cognito ja tem captcha proprio, isso e camada extra clientside.
  const honeypotRef = useRef(null);

  const submit = (e) => {
    e.preventDefault();
    // Honeypot: bot foi pego.
    if (honeypotRef.current && honeypotRef.current.value) {
      // Erro generico — nao revela que detectamos o bot.
      setLocalError('Preencha login e senha para continuar.');
      return;
    }
    // Guards de double-submit:
    // - loading: request em voo, qualquer Enter/click extra ignorado
    // - inCooldown: rate limit acionado, espera passar
    // Sem isso, Enter rapido durante o submit dispararia 2 Auth.signIn
    // paralelos (o segundo ganharia race e tentaria com state stale).
    if (loading || inCooldown) return;
    if (!username.trim() || !password) {
      setLocalError('Preencha login e senha para continuar.');
      return;
    }
    // Se o usuario digitou algo com '@' assumimos que e email e validamos
    // formato. Se nao tem '@', e username puro — passa direto (Cognito
    // aceita ambos).
    if (looksLikeEmail(username) && !isValidEmail(username)) {
      setLocalError('Formato de e-mail inválido.');
      return;
    }
    handleLogin();
  };

  // Flag de erro por campo — usado pra colorir o ctrl quando o
  // operador submeteu sem preencher.
  const hasUserError = !!localError && !username.trim();
  const hasPassError = !!localError && !password;

  return (
    <s.Stage data-screen-label="00 Login">
      {/* LEFT — brand panel (escondido em <=1080px via styled) */}
      <s.BrandSide>
        <s.BrandBg $src={bgLogin} />
        <s.BrandOverlay />

        <s.BrandTop>
          <s.BrandMark>
            <img src={logoNexus} alt="Nexus" />
          </s.BrandMark>
        </s.BrandTop>

        <s.BrandHero>
          <s.Eyebrow>
            <span className="pulse" />
            Centro de Controle Operacional
          </s.Eyebrow>
          <s.HeroTitle>
            Bem-vindo
            <br />
            <span className="accent">de volta.</span>
          </s.HeroTitle>
          <s.HeroLead>
            Monitoramento em tempo real de UCHAs, EBAs e estações em toda a
            rede. Entre para visualizar geração, consumo e alarmes.
          </s.HeroLead>

          <s.StatusRow>
            <s.StatusCell>
              <span className="k">Plantas online</span>
              <span className="v">
                38 <span className="u">/ 41</span>
              </span>
            </s.StatusCell>
            <s.StatusCell>
              <span className="k">Vazão agora</span>
              <span className="v">
                9.124 <span className="u">L/s</span>
              </span>
            </s.StatusCell>
            <s.StatusCell>
              <span className="k">Geração 24h</span>
              <span className="v">
                +21,6 <span className="u">GWh</span>
              </span>
            </s.StatusCell>
          </s.StatusRow>
        </s.BrandHero>

        <s.BrandFoot>
          <span>© 2026 Higra. Todos os direitos reservados.</span>
        </s.BrandFoot>
      </s.BrandSide>

      {/* RIGHT — form */}
      <s.FormSide>
        <s.FormWrap>
          <s.FormHead>
            <s.FormHeadLbl>
              {IBolt} Entrar
            </s.FormHeadLbl>
            <s.FormTitle>Acesse sua conta</s.FormTitle>
            <s.FormSub>
              Não possui acesso?{' '}
              <a href="/forgot-password">
                Fale com o administrador da sua unidade.
              </a>
            </s.FormSub>
          </s.FormHead>

          <s.Form onSubmit={submit}>
            {/* Honeypot anti-bot — invisivel pra humanos (offscreen +
                aria-hidden + tabindex=-1 + autoComplete=off). Bots
                preencheriam por conta do name='url' generico. */}
            <input
              ref={honeypotRef}
              type="text"
              name="url"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{
                position: 'absolute',
                width: 1,
                height: 1,
                opacity: 0,
                pointerEvents: 'none',
                left: '-9999px',
              }}
            />
            <s.Field $error={hasUserError}>
              <s.FieldLabel>
                Login
                <span className="hint">E-mail ou usuário</span>
              </s.FieldLabel>
              <s.Ctrl>
                {IUser}
                <input
                  ref={usernameInputRef}
                  type="text"
                  placeholder="seu.usuario"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => handleUsernameChange(e.target.value)}
                  aria-invalid={hasUserError}
                  aria-describedby={error ? 'login-form-error' : undefined}
                />
              </s.Ctrl>
            </s.Field>

            <s.Field $error={hasPassError || !!reduxError}>
              <s.FieldLabel>
                Senha
                <a href="/forgot-password">Esqueci minha senha</a>
              </s.FieldLabel>
              <s.Ctrl>
                {ILock}
                <input
                  ref={passwordInputRef}
                  type={showPass ? 'text' : 'password'}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  onKeyDown={handlePassKeyEvent}
                  onKeyUp={handlePassKeyEvent}
                  aria-invalid={hasPassError || !!reduxError}
                  aria-describedby={
                    capsLockOn
                      ? 'login-caps-hint'
                      : error
                      ? 'login-form-error'
                      : undefined
                  }
                />
                <s.ToggleEye
                  onClick={() => setShowPass((p) => !p)}
                  title={showPass ? 'Ocultar' : 'Mostrar'}
                  aria-label={showPass ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showPass ? IEyeOff : IEye}
                </s.ToggleEye>
              </s.Ctrl>
              {capsLockOn && (
                <s.CapsLockHint id="login-caps-hint" role="status">
                  <svg
                    viewBox="0 0 24 24"
                    width="14"
                    height="14"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="m6 14 6-6 6 6M6 18h12" />
                  </svg>
                  Caps Lock está ligado
                </s.CapsLockHint>
              )}
              {error && (
                <s.Hint id="login-form-error" role="alert">
                  {IAlert}
                  {error}
                </s.Hint>
              )}
            </s.Field>

            <s.RememberRow>
              <s.Check
                className={remember ? 'on' : ''}
                onClick={() => setRemember((r) => !r)}
                aria-pressed={remember}
              >
                <span className="box">{ICheckMark}</span>
                Manter conectado
              </s.Check>
            </s.RememberRow>

            <s.Submit
              disabled={loading || inCooldown || !username || !password}
              aria-busy={loading}
            >
              {inCooldown ? (
                `Aguarde ${cooldownLeftSec}s`
              ) : loading ? (
                'Entrando…'
              ) : (
                <>
                  Entrar {IArrow}
                </>
              )}
            </s.Submit>
          </s.Form>
        </s.FormWrap>

        <s.FormFoot>
          <s.Vers>{APP_VERSION_LABEL}</s.Vers>
        </s.FormFoot>
      </s.FormSide>
    </s.Stage>
  );
};

export default withRouter(Login);
